import { useQuery } from '@tanstack/react-query';
import { channelWa, flows, identity, inbox } from '@repletoryreach/sdk-api';
import type { ChecklistItem, Guide } from '../types';
import { builders } from './helpers';

// The Inbox guide (8 October 2026): the conversation list, a thread, replying, the bot and take over, assigning,
// notes, tickets and bookings from a conversation. Words under guide.inbox.*. Most steps need a conversation open,
// so they name the first row as the thing to open.

const { link, howLink, step, how, section, howTo } = builders('inbox');
const L = {
  numbers: link('numbers', '/numbers'),
  compose: link('compose', '/inbox/compose'),
  templates: link('templates', '/templates'),
  canned: link('canned', '/settings/canned-replies'),
  team: link('team', '/settings/team'),
  hours: link('hours', '/settings/organisation'),
  notifications: link('notifications', '/settings/notifications'),
  assistant: link('assistant', '/settings/assistant'),
  flows: link('flows', '/flows'),
  tickets: link('tickets', '/tickets'),
  ticketSettings: link('ticketSettings', '/tickets/settings'),
  bookings: link('bookings', '/bookings'),
  contacts: link('contacts', '/contacts'),
  usage: link('usage', '/billing/usage'),
  howReply: howLink('howReply', 'reply'),
  howTakeover: howLink('howTakeover', 'takeover'),
  howAssign: howLink('howAssign', 'assign')
};

/** A conversation to show: the first in the list. */
const ROW = 'inbox-row';
/** The conversation settings: the header's Settings button when a conversation is open, else the first row. */
const SIDE = 'inbox-settings,inbox-row';

function useInboxChecklist(): { items: ChecklistItem[]; loading: boolean } {
  const numbers = useQuery({ queryKey: ['numbers'], queryFn: () => channelWa.listPhoneNumbers(), staleTime: 60_000 });
  const sites = useQuery({ queryKey: ['web-sites'], queryFn: () => flows.listWebSites(), staleTime: 60_000 });
  const convs = useQuery({ queryKey: ['conversations', 'guide'], queryFn: () => inbox.listConversations({ limit: 1 }), staleTime: 60_000 });
  const canned = useQuery({ queryKey: ['canned-replies', 'guide'], queryFn: () => inbox.listCannedReplies({ limit: 1 }), staleTime: 60_000 });
  const hours = useQuery({ queryKey: ['business-hours'], queryFn: () => identity.getBusinessHours(), staleTime: 60_000 });
  const people = useQuery({ queryKey: ['team', 'guide'], queryFn: () => identity.listUsers({ limit: 2 }), staleTime: 60_000 });
  const item = (id: string, to: string, done: boolean | undefined, optional = false): ChecklistItem => ({
    id, to, done, optional, titleKey: `guide.inbox.check.${id}.title`, hintKey: `guide.inbox.check.${id}.hint`
  });
  const channel = numbers.data && sites.data ? (numbers.data.data?.length ?? 0) + (sites.data.data?.length ?? 0) > 0 : undefined;
  const items = [
    item('channel', '/numbers', channel),
    item('hours', '/settings/organisation', hours.data ? hours.data.data?.configured === true : undefined),
    item('canned', '/settings/canned-replies', canned.data ? (canned.data.meta?.total ?? canned.data.data?.length ?? 0) > 0 : undefined),
    item('notifications', '/settings/notifications', undefined),
    item('first', '/inbox', convs.data ? (convs.data.meta?.total ?? convs.data.data?.length ?? 0) > 0 : undefined),
    item('team', '/settings/team', people.data ? (people.data.meta?.total ?? people.data.data?.length ?? 0) > 1 : undefined, true)
  ];
  return { items, loading: [numbers, sites, convs, canned, hours, people].some((q) => q.isPending) };
}

export const INBOX_GUIDE: Guide = {
  id: 'inbox',
  version: 1,
  titleKey: 'guide.inbox.title',
  introKey: 'guide.inbox.intro',
  routes: ['/inbox'],
  tour: [
    step('welcome', undefined, undefined, [L.numbers]),
    step('list', '/inbox', 'inbox-list,inbox-tabs'),
    step('tabs', '/inbox', 'inbox-tabs'),
    step('available', '/inbox', 'inbox-available', [L.howAssign]),
    step('search', '/inbox', 'inbox-search'),
    step('newMessage', '/inbox', 'inbox-new', [L.compose]),
    { ...step('header', '/inbox', 'inbox-header'), open: ROW },
    { ...step('actions', '/inbox', 'inbox-actions', [L.tickets, L.bookings]), open: ROW },
    { ...step('thread', '/inbox', 'inbox-thread'), open: ROW },
    { ...step('footer', '/inbox', 'inbox-footer', [L.canned, L.templates]), open: ROW },
    { ...step('sidebar', '/inbox', 'inbox-sidebar', [L.howAssign]), open: SIDE },
    { ...step('notes', '/inbox', 'inbox-side-notes'), open: SIDE },
    step('help', '/inbox', 'inbox-help')
  ],
  howTos: [
    howTo('reply', [
      how('reply', 'open', '/inbox', 'inbox-list,inbox-tabs'),
      how('reply', 'window', '/inbox', 'inbox-header', { open: ROW, links: [L.templates] }),
      how('reply', 'write', '/inbox', 'inbox-footer', { open: ROW }),
      how('reply', 'canned', '/inbox', 'inbox-footer', { open: ROW, links: [L.canned] }),
      how('reply', 'resolve', '/inbox', 'inbox-actions', { open: ROW, links: [L.flows] })
    ]),
    howTo('takeover', [
      how('takeover', 'what', '/inbox', 'inbox-actions', { open: ROW }),
      how('takeover', 'bot', '/inbox', 'inbox-side-details', { open: SIDE, links: [L.assistant] }),
      how('takeover', 'handback', '/inbox', 'inbox-actions', { open: ROW })
    ]),
    howTo('assign', [
      how('assign', 'details', '/inbox', 'inbox-side-details', { open: SIDE, links: [L.team] }),
      how('assign', 'transfer', '/inbox', 'inbox-actions', { open: ROW }),
      how('assign', 'notes', '/inbox', 'inbox-side-notes', { open: SIDE }),
      how('assign', 'ticket', '/inbox', 'inbox-actions', { open: ROW, links: [L.ticketSettings, L.bookings] })
    ])
  ],
  sections: [
    section('overview', 3, 1, [L.numbers, L.compose]),
    section('list', 3, 1, []),
    section('channels', 3, 1, [L.numbers]),
    section('window', 3, 1, [L.templates]),
    section('replying', 3, 2, [L.canned]),
    section('bot', 3, 1, [L.assistant, L.flows, L.howTakeover]),
    section('assign', 3, 1, [L.team, L.howAssign]),
    section('notes', 2, 1, [L.contacts]),
    section('work', 2, 1, [L.tickets, L.bookings]),
    section('resolve', 3, 1, [L.flows]),
    section('block', 2, 1, []),
    section('costs', 2, 1, [L.usage]),
    section('trouble', 5, 0, [L.numbers, L.notifications, L.howReply])
  ],
  useChecklist: useInboxChecklist
};
